/**
 * useServerVersion (T29) — read the version of the backend the client actually
 * reached, from the public `/api/health` endpoint (D8). Shared by the App
 * settings Server row and the login footer.
 *
 * Offline-first: `retry: false`, an `AbortController` timeout, and a
 * `catch → null` so an unreachable backend renders a quiet `—` instead of a
 * crash or a spinner that never resolves. Long `staleTime` — the deployed
 * backend version barely changes within a session.
 */

import { useQuery } from '@tanstack/react-query';

export interface ServerVersion {
  semver: string;
  build: string;
  sha: string;
  /**
   * T29.2 (D7) — the backend's own build date, from `/api/health` `date`
   * (APP_COMMIT_DATE). Empty string when the backend didn't report one (local /
   * unstamped); the Server row's meta line omits it then.
   */
  date: string;
}

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL ?? 'http://localhost:3001';
const TIMEOUT_MS = 4000;
const STALE_MS = 5 * 60 * 1000;

// Exported for unit test (T29.2) — the parse + offline-fallback logic is the
// behaviour worth pinning; the hook is a thin TanStack wrapper around it.
export async function fetchServerVersion(): Promise<ServerVersion | null> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), TIMEOUT_MS);
  try {
    const res = await fetch(`${API_BASE_URL}/api/health`, {
      signal: controller.signal,
    });
    if (!res.ok) return null;
    const body = (await res.json()) as {
      semver?: string;
      build?: string;
      commit?: string;
      date?: string;
    };
    if (!body || typeof body.semver !== 'string') return null;
    return {
      semver: body.semver,
      build: body.build ?? 'unknown',
      // The health payload calls the short SHA `commit`; the client surfaces
      // it as `sha` to match the local build-stamp vocabulary.
      sha: body.commit ?? 'unknown',
      // T29.2 (D7) — the backend's own build date; '' when unstamped.
      date: typeof body.date === 'string' ? body.date : '',
    };
  } catch {
    // Offline / aborted / malformed — render a quiet dash, never throw.
    return null;
  } finally {
    clearTimeout(timer);
  }
}

export function useServerVersion(): ServerVersion | null {
  const query = useQuery({
    queryKey: ['server-version'],
    queryFn: fetchServerVersion,
    retry: false,
    staleTime: STALE_MS,
  });
  return query.data ?? null;
}
